import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cn } from '@/lib/utils'

type Variant = 'orange' | 'lightblue' | 'gray'
type Size = 'M' | 'L'

export type Button3DProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant
  size?: Size
}

const variantClasses: Record<Variant, string> = {
  orange:
    'bg-lc-orange-900 text-white shadow-lc-3d-orange active:shadow-lc-3d-orange-active',
  lightblue:
    'bg-lc-lightblue-900 text-white shadow-lc-3d-lightblue active:shadow-lc-3d-lightblue-active',
  gray:
    'bg-lc-gray-100 text-lc-gray-700 shadow-lc-3d-gray active:shadow-lc-3d-gray-active',
}

const sizeClasses: Record<Size, string> = {
  M: 'px-4 py-[9px]',
  L: 'px-6 py-[13px]',
}

export const Button3D = forwardRef<HTMLButtonElement, Button3DProps>(
  function Button3D(
    { variant = 'orange', size = 'L', disabled, className, type = 'button', children, ...rest },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled}
        className={cn(
          'inline-flex items-center justify-center rounded-lc-lg',
          'font-bold text-[20px] leading-[1.5] tracking-[-0.6px]',
          'transition-[transform,box-shadow] duration-100 ease-out',
          'active:translate-y-[4px]',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-lightblue-900',
          sizeClasses[size],
          disabled
            ? 'bg-lc-gray-400 text-white shadow-lc-3d-disable pointer-events-none'
            : variantClasses[variant],
          className,
        )}
        {...rest}
      >
        {children}
      </button>
    )
  },
)
